import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";

import {
  IApiAuthPermissoesUsuarioResponse,
  useApiAuthPermissoesUsuario,
} from "@hooks/api/auth";
import { ApiInterceptorError } from "@classes/ApiInterceptorError";

import useAuth from "@hooks/useAuth";
import { useCorban } from "@hooks/useCorban";
import { PRODUCT_TYPE } from "@utils/enums";
import { getProductTypeFromProductCode } from "@utils/product";
import { getValidateRegisterState, User } from "@contexts/Auth";
import {
  IApiV2ProductsResponse,
  useApiV2Products,
} from "@hooks/api/v2/products";
import { featureFlags } from "@settings";

type PermissionsContext = {
  permissions: Record<string, string>;
  routes: Record<string, boolean>;
  allowedRoutes: string[];
  homeRoute: string;
  isReady: boolean;
  hasPermission: (permission: string) => boolean;
  acceptedTerms: boolean;
  loading: boolean;
};

export const PermissionsContext = createContext<PermissionsContext>({
  permissions: {},
  routes: {},
  allowedRoutes: [],
  homeRoute: "",
  isReady: false,
  hasPermission: () => false,
  acceptedTerms: false,
  loading: true,
});

export const PermissionsContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [
    fetchPermissoesUsuario,
    {
      data: fetchPermissionsData,
      loading: permissionsLoading,
      reset: resetFetchPermissions,
    },
  ] = useApiAuthPermissoesUsuario();

  const enableProductsV2 = featureFlags.enablePrivateConsigV2;

  const [
    fetchProductsV2,
    {
      data: fetchProductsV2Data,
      isFetched: productsV2Fetched,
      loading: productsV2Loading,
      reset: resetFetchProductsV2,
    },
  ] = useApiV2Products();

  const {
    user,
    updateUser,
    setProducts,
    hasProduct,
    hasProductV2,
    loading: userLoading,
    onExpireSession,
  } = useAuth();

  const { isReady: isReadyCorban } = useCorban();

  const [permissions, setPermissions] = useState<
    PermissionsContext["permissions"]
  >({});
  const [loading, setLoading] = useState(true);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [productsV2RequestFinished, setProductsV2RequestFinished] =
    useState(false);

  const clearData = useCallback(() => {
    setPermissions({});
    setAcceptedTerms(false);
    setProductsV2RequestFinished(false);
    resetFetchPermissions();
    resetFetchProductsV2();
  }, [resetFetchPermissions, resetFetchProductsV2]);

  const hasPermission = useCallback(
    (permission: string) => !!permissions[permission],
    [permissions],
  );

  const isProductsV2Ready = enableProductsV2
    ? productsV2RequestFinished || !!fetchProductsV2Data || productsV2Fetched
    : true;

  const routes: Record<string, boolean> = useMemo(() => {
    return {
      "/servicos": hasPermission("add_contrato"),
      "/inss/aumento-margem":
        hasProduct(PRODUCT_TYPE.MARGEM_LIVRE_AUMENTO) &&
        hasPermission("add_contrato"),
      "/inss/aumento-margem-lote":
        hasProduct(PRODUCT_TYPE.MARGEM_LIVRE_AUMENTO) &&
        hasPermission("add_contrato"),
      "/inss/margem-livre":
        hasProduct(PRODUCT_TYPE.MARGEM_LIVRE) && hasPermission("add_contrato"),
      "/inss/portabilidade":
        hasProduct(PRODUCT_TYPE.PORTABILIDADE) && hasPermission("add_contrato"),
      "/inss/portabilidade-refin":
        hasProduct(PRODUCT_TYPE.PORTABILIDADE_REFIN) &&
        hasPermission("add_contrato"),
      "/inss/refinanciamento":
        hasProduct(PRODUCT_TYPE.REFINANCIAMENTO) &&
        hasPermission("add_contrato"),
      "/receba":
        hasProduct(PRODUCT_TYPE.RECEBA) && hasPermission("add_contrato"),
      "/consignado-privado": hasProduct(PRODUCT_TYPE.CONSIGNADO_PRIVADO),
      "/v2/consignado-privado":
        hasProductV2(PRODUCT_TYPE.CONSIGNADO_PRIVADO) &&
        hasPermission("add_contrato"),
      "/cartao/cartao-beneficio":
        hasProduct(PRODUCT_TYPE.CARTAO_BENEFICIO) &&
        hasPermission("add_contrato"),
      "/cartao/agregacao-margem":
        hasProduct(PRODUCT_TYPE.AGREGACAO_MARGEM) &&
        hasPermission("add_contrato"),
      "/saque-complementar":
        hasProduct(PRODUCT_TYPE.CARTAO_BENEFICIO) &&
        hasPermission("add_contrato"),
      "/emprestimo-privado": hasProduct(PRODUCT_TYPE.CONSIGNADO_PRIVADO),
      "/emprestimo-privado/detalhes": hasProduct(
        PRODUCT_TYPE.CONSIGNADO_PRIVADO,
      ),
      "/contratos": true,
      "/usuarios": hasPermission("happy_can_create_user"),
      "/usuarios/novo": hasPermission("happy_can_create_user"),
      "/selecao-corban": true,
      "/termos-de-uso": true,
      "/politica-de-privacidade": true,
      "/materiais-de-apoio": true,
      "/materiais-de-apoio/visualizar-documento": true,
      "/portabilidade-ataque": hasPermission("can_acess_portability_attacks"),
      "/portabilidade-ataque/contratos": hasPermission(
        "can_acess_portability_attacks",
      ),
      "/portabilidade-ataque/acompanhamento": hasPermission(
        "can_acess_portability_attacks",
      ),
      "/sem-acesso": true,
      "/inss": [
        PRODUCT_TYPE.MARGEM_LIVRE,
        PRODUCT_TYPE.PORTABILIDADE,
        PRODUCT_TYPE.PORTABILIDADE_REFIN,
        PRODUCT_TYPE.REFINANCIAMENTO,
      ].some(hasProduct),
      "/cartao": [
        PRODUCT_TYPE.CARTAO_BENEFICIO,
        PRODUCT_TYPE.AGREGACAO_MARGEM,
      ].some(hasProduct),
      "/treinamentos": true,
      "/nao-me-perturbe": true,
    };
  }, [hasProduct, hasProductV2, hasPermission]);

  const allowedRoutes = useMemo(() => {
    return Object.keys(routes).filter((route) => routes[route]);
  }, [routes]);

  const homeRoute = useMemo(() => allowedRoutes[0] || "", [allowedRoutes]);

  const fetchPermissions = useCallback(async () => {
    setLoading(true);
    setProductsV2RequestFinished(!enableProductsV2);

    try {
      let data: IApiAuthPermissoesUsuarioResponse;
      let dataV2: IApiV2ProductsResponse | null = null;

      if (enableProductsV2) {
        const fetchProductsPermissionsV2 = async () => {
          try {
            const response = await fetchProductsV2({
              shouldSkipErrorSnackbar: () => true,
            });

            return response;
          } catch (e) {
            console.error("[fetchPermissions] error fetching products v2", e);
            return null;
          } finally {
            setProductsV2RequestFinished(true);
          }
        };

        const [responseV1, responseV2] = await Promise.all([
          fetchPermissoesUsuario(),
          fetchProductsPermissionsV2(),
        ]);

        data = responseV1.data;
        dataV2 = responseV2?.data ?? null;
      } else {
        const response = await fetchPermissoesUsuario();
        data = response.data;
      }

      const userData: Partial<User> = {
        name: data.name,
        uniqueId: data.unique_id,
        identifier: data.identifier,
        enableFaceMatch: data.enable_face_match,
        isChecked: data.is_checked,
        checkUrl: data.check_url,
        supportChat: data.possui_chat_suporte,
        allowIlliterate: !!data.corban?.permite_analfabeto,
        allowLegalRepresentative: !!data.corban?.permite_representante,
        allowCardLegalRepresentative:
          !!data.corban?.permite_representante_cartao,
        id: data.id,
        groups: data.groups,
        productsV2: dataV2?.data?.produtos ?? [],
        validateRegister: getValidateRegisterState({
          isChecked: data.is_checked,
          isLinked: data.cadastro_validation?.is_linked,
          linkedUrl: data.cadastro_validation?.formalization_url,
          statusLabel: data.cadastro_validation?.status_label,
        }),
      };

      updateUser(userData);

      setPermissions(
        data.permissions.reduce<Record<string, string>>(
          (permissionsHashList, permission) => ({
            ...permissionsHashList,
            [permission]: "true",
          }),
          {},
        ),
      );

      setProducts(
        data.produtos.map((product) => ({
          nome: product.nome,
          tipo_produto: getProductTypeFromProductCode(product.cd_produto),
        })),
      );

      setAcceptedTerms(data.termo_uso_privacidade);
    } catch (e) {
      if (
        !(e instanceof ApiInterceptorError) ||
        !axios.isCancel(e.sourceError)
      ) {
        clearData();
        console.error("[fetchPermissions] error", { error: e });
        onExpireSession("");
      }
    } finally {
      setLoading(false);
    }
  }, [
    enableProductsV2,
    updateUser,
    setProducts,
    fetchPermissoesUsuario,
    fetchProductsV2,
    clearData,
    onExpireSession,
  ]);

  useEffect(() => {
    if (userLoading) return;

    if (!user?.refreshToken) {
      clearData();
      setLoading(false);
      return;
    }

    if (isReadyCorban) {
      void fetchPermissions();
    }
  }, [
    user?.refreshToken,
    userLoading,
    fetchPermissions,
    clearData,
    isReadyCorban,
  ]);

  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        routes,
        allowedRoutes,
        homeRoute,
        isReady: !!fetchPermissionsData && isProductsV2Ready,
        hasPermission,
        acceptedTerms,
        loading:
          loading ||
          permissionsLoading ||
          (enableProductsV2 && productsV2Loading),
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};
